import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import { formatString } from './utils/StringUtils';
import ExcelUploadModal from './components/modalServices/ExcelUploadModal';
import DefaultModal from './components/modals/DefaultModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import DfdFindItemModal from './components/modalServices/DfdFindItemModal';
import { formatCurrency, unformatCurrency, formatQuantidade } from './utils/formatMoeda';
import LoadingModal from './components/modals/LoadingModal';
import useProgressModal from './components/hooks/useProgressModal';
import ProgressModal from './components/modals/ProgressModal';
import Toolbar from './components/Toolbar';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ResponsiveShadow from './components/ResponsiveShadow';

function DfdLoteItens() {
  const [dfd, setDfd] = useState({
    cnpj: '',
    dfdNum: '',
    dataContratacao: '',
    requisitante: '',
    objeto: '',
    prioridade: '',
    justificativaPrioridade: '',
    justificativaDemanda: '',
    tipo: '',
    usuario: '',
  });
  const [dfdLote, setDfdLote] = useState({
    numLote: '',
  });
  const [dfdItens, setDfdItens] = useState([]);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [categoriaItem, setCategoriaItem] = useState([]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [showFindModal, setShowFindModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const token = localStorage.getItem('token');
  const location = useLocation();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const navigate = useNavigate();
  const {
    progressState,
    showProgressModal,
    updateProgress,
    hideProgressModal,
  } = useProgressModal();
  const cancelSave = useRef(false);

  const normalizeText = str => {
    return (str || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .toUpperCase()
      .trim();
  };

  const filteredDfdItens = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm);
    return dfdItens.filter(
      item =>
        normalizeText(item.item?.toString()).includes(normalizedSearch) ||
        normalizeText(item.descricao).includes(normalizedSearch)
    );
  }, [searchTerm, dfdItens]);

  const totalItems = filteredDfdItens.length;
  const { setPagination } = usePagination10(totalItems);
  const { startIndex, rowsPerPage } = setPagination;
  const selectedDfdItens = filteredDfdItens.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  useEffect(() => {
    if (location.state) {
      if (location.state.dfd) setDfd(location.state.dfd);
      if (location.state.dfdLote) {
        setDfdLote(location.state.dfdLote);
      }
    }
    // eslint-disable-next-line
  }, [location.state]);

  useEffect(() => {
    setLoading(true);
    const dfdNumParam = dfd.num || dfd.dfdNum || dfdLote.num || dfdLote.dfdNum;
    const loteNumParam = dfdLote.numLote || dfdLote.id;
    if (dfd.cnpj !== '' && dfdNumParam && loteNumParam) {
      axios
        .get(`${API_BASE_URL.DFD_ITEM}/demanda`, {
          params: {
            cnpj: dfd.cnpj,
            num: dfdNumParam,
            loteNum: loteNumParam,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          const formattedData = result.data.map(item => ({
            ...item,
            vlrUnit: formatCurrency(item.vlrUnit),
          }));
          setDfdItens(formattedData);
        })
        .catch(error => {
          console.error('Erro ao obter listas:', error);
        });

      axios
        .get(`${API_BASE_URL.UNIDADE_MEDIDA}/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setUnidades(result.data);
        })
        .catch(error => {
          console.error('Erro ao obter unidades:', error);
        });

      axios
        .get(`${API_BASE_URL.CATEGORIA_ITEM}/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setCategoriaItem(result.data);
        })
        .catch(error => {
          console.error('Erro ao obter categorias:', error);
        });
    }
    setLoading(false);
  }, [dfd, dfdLote, shouldFetch, token]);

  const handleSearchChange = e => {
    setSearchTerm(e.target.value.toUpperCase());
  };

  function handleDelete(dfdToDelete) {
    showConfirmModal('delete', () => handleDeleteConfirm(dfdToDelete));
  }

  function handleDeleteConfirm(itemToDelete) {
    axios
      .delete(`${API_BASE_URL.DFD_ITEM}/delete`, {
        params: {
          cnpj: itemToDelete.cnpj,
          dfdNum: itemToDelete.dfdNum,
          id: itemToDelete.id,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        setShouldFetch(!shouldFetch);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir o item:', error);
        showDefaultModal('error', 'Erro ao excluir o registro!');
      });
  }

  function calculateTotal(quantidade, vlrUnit) {
    const quantidadeNum = parseFloat(quantidade) || 0;
    const vlrUnitNum = unformatCurrency(vlrUnit) || 0;
    return formatCurrency(quantidadeNum * vlrUnitNum);
  }

  const handleInputChange = (localIndex, field, value) => {
    const globalIndex = dfdItens.findIndex(
      item => item.id === selectedDfdItens[localIndex].id
    );
    if (globalIndex === -1) return;

    const updated = [...dfdItens];
    if (field === 'vlrUnit') {
      updated[globalIndex][field] = formatCurrency(unformatCurrency(value));
    } else {
      updated[globalIndex][field] = value;
    }

    setDfdItens(updated);
  };

  const saveBatch = async batch => {
    const itemsToSave = batch.map(item => ({
      ...item,
      vlrUnit: unformatCurrency(item.vlrUnit),
    }));

    return axios.post(`${API_BASE_URL.DFD_ITEM}/saveAll`, itemsToSave, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: 30000,
    });
  };

  const handleSaveAll = async () => {
    cancelSave.current = false;
    const batchSize = 100;
    const totalItems = dfdItens.length;

    // Resetar o estado completamente antes de iniciar
    showProgressModal('Salvando Itens do DFD', totalItems, () => {
      cancelSave.current = true;
      hideProgressModal();
    });

    // Forçar uma atualização imediata do estado
    await new Promise(resolve => setTimeout(resolve, 50));

    const results = {
      success: 0,
      failures: 0,
      errorDetails: [],
    };

    try {
      for (let i = 0; i < dfdItens.length; i += batchSize) {
        if (cancelSave.current) break;

        const batch = dfdItens.slice(i, i + batchSize);

        try {
          await saveBatch(batch);
          results.success += batch.length;
          // Atualizar progresso de forma mais eficiente
          const currentProgress = Math.min(i + batch.length, totalItems);
          updateProgress(currentProgress);

          // Forçar renderização entre lotes
          await new Promise(resolve => setTimeout(resolve, 0));
        } catch (error) {
          results.failures += batch.length;
          results.errorDetails.push({
            batch: Math.floor(i / batchSize) + 1,
            error: error.response?.data?.message || error.message,
          });
          updateProgress(Math.min(i + batch.length, totalItems), 1);
        }
      }

      // Adicionar pequeno delay antes de esconder
      await new Promise(resolve => setTimeout(resolve, 300));
      hideProgressModal();

      if (!cancelSave.current && results.success > 0) {
        setShouldFetch(prev => !prev);
        showDefaultModal(
          'info',
          `Processo concluído! Sucessos: ${results.success}, Falhas: ${results.failures}`,
          results.errorDetails.length > 0 && {
            title: 'Erros encontrados',
            content: (
              <div className="small">
                <ul>
                  {results.errorDetails.map((err, idx) => (
                    <li key={idx}>
                      Lote {err.batch}: {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            ),
          }
        );
      }
    } catch (error) {
      hideProgressModal();
      showDefaultModal('error', 'Erro durante o processamento');
    }
  };

  async function handleExportToExcel() {
    try {
      const response = await axios.post(
        `${API_BASE_URL.DFD}/exportExcel`,
        dfd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `DFD ${formatString(dfd.dfdNum, 4)}.xlsx`;
      link.click();
    } catch (error) {
      console.error('Erro ao baixar o arquivo:', error);
    }
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title={`Itens do Lote Nº ${dfdLote.numLote || dfdLote.id} do DFD Nº ${dfdLote ? formatString(dfdLote.dfdNum, 4) : '--'}`} icon="bi-boxes" backButton={false} closeRoute="/dfd_lotes" closeState={{ dfd }} />
        <PageContent style={{ paddingBottom: 0 }}>
          <Toolbar
            actions={(
              dfd.tramitacao === 'EM ELABORAÇÃO' || dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES'
              ) ? [
                { key: 'add', label: 'Adicionar Item', icon: 'bi-plus-circle', variant: 'success', onClick: () => setShowFindModal(true) },
              { key: 'saveAll', label: progressState.show ? 'Salvando...' : 'Salvar Todos', icon: 'bi-save', variant: 'primary', onClick: handleSaveAll, disabled: progressState.show || isLoading }
            ] : []}
            onSearch={(v) => handleSearchChange({ target: { value: v } })}
            searchPlaceholder="Digite código ou descrição..."
            searchWidth="250px"
          />

          <LoadingModal loading={isLoading} />

          <TableContainer tableClass="table-custom-radius">
        <TableHeader columns={[
          { label: 'Grupo', width: '5%', align: 'center' },
          { label: 'Classe', width: '5%', align: 'center' },
          { label: 'Código', width: '5%', align: 'center' },
          { label: 'Descrição', align: 'center' },
          { label: 'Categoria', width: '10%', align: 'center' },
          { label: 'Unidade', width: '10%', align: 'center' },
          { label: 'Quantidade', width: '10%', align: 'center' },
          { label: 'Valor Unitário', width: '10%', align: 'center' },
          { label: 'Valor Total', width: '10%', align: 'center' },
          ...((dfd.tramitacao === 'EM ELABORAÇÃO' || dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES') ? [{ label: 'Ação', align: 'center' }] : []),
        ]} />
        <tbody>
          {!isLoading && selectedDfdItens.length > 0 ? (
            selectedDfdItens.map((c, index) => {
              return (
                <tr key={`${c.cnpj}-${c.dfdNum}-${c.id}`}>
                  <td style={{ width: '5%' }}>{c.grupo}</td>
                  <td style={{ width: '5%' }}>{c.classe}</td>
                  <td style={{ width: '5%' }}>{c.item}</td>
                  <td>{c.descricao}</td>
                  <td className="text-end">
                    {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                    dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                      <select
                        name="categoria"
                        value={c.categoria}
                        onChange={e =>
                          handleInputChange(index, 'categoria', e.target.value)
                        }
                        className={`form-select form-select-sm${!c.categoria ? ' border-warning border-2' : ''}`}
                        style={{ minWidth: '100px' }}
                        disabled={progressState.show}
                      >
                        <option value="">Selecione...</option>
                        {categoriaItem.map(categoria => (
                          <option
                            key={categoria.id}
                            value={categoria.descricao}
                          >
                            {categoria.descricao}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{c.categoria}</span>
                    )}
                  </td>
                  <td className="text-end">
                    {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                    dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                      <select
                        name="unidade"
                        value={c.unidade}
                        onChange={e =>
                          handleInputChange(index, 'unidade', e.target.value)
                        }
                        className={`form-select form-select-sm${!c.unidade ? ' border-warning border-2' : ''}`}
                        style={{ minWidth: '100px' }}
                        disabled={progressState.show}
                      >
                        <option value="">Selecione...</option>
                        {unidades.map(unidade => (
                          <option key={unidade.id} value={unidade.descricao}>
                            {unidade.descricao.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{c.unidade.toUpperCase()}</span>
                    )}
                  </td>
                  <td className="text-end">
                    {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                    dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                      <input
                        type="number"
                        name="quantidade"
                        value={c.quantidade ?? ''}
                        onChange={e =>
                          handleInputChange(index, 'quantidade', e.target.value)
                        }
                        className={`form-control form-control-sm text-end${(!c.quantidade || Number(c.quantidade) === 0) ? ' border-warning border-2' : ''}`}
                        style={{ minWidth: '100px' }}
                        min="0"
                        disabled={progressState.show}
                      />
                    ) : (
                      <span>{formatQuantidade(c.quantidade)}</span>
                    )}
                  </td>
                  <td className="text-end">
                    {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                    dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                      <input
                        type="text"
                        name="vlrUnit"
                        value={c.vlrUnit ?? ''}
                        onChange={e =>
                          handleInputChange(index, 'vlrUnit', e.target.value)
                        }
                        className={`form-control form-control-sm text-end${(!c.vlrUnit || unformatCurrency(c.vlrUnit) === 0) ? ' border-warning border-2' : ''}`}
                        style={{ minWidth: '100px' }}
                        disabled={progressState.show}
                      />
                    ) : (
                      <span>{c.vlrUnit ?? ''}</span>
                    )}
                  </td>
                  <td className="text-end">
                    {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                    dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                      <input
                        disabled
                        type="text"
                        name="vlrTotal"
                        value={calculateTotal(c.quantidade, c.vlrUnit)}
                        className="form-control form-control-sm text-end"
                        style={{ minWidth: '100px' }}
                      />
                    ) : (
                      <span>{calculateTotal(c.quantidade, c.vlrUnit)}</span>
                    )}
                  </td>
                  {(dfd.tramitacao === 'EM ELABORAÇÃO' ||
                    dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES') && (
                    <td>
                      <button
                        onClick={() => handleDelete(c)}
                        className="btn btn-sm"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Excluir item"
                        disabled={progressState.show}
                      >
                        <i className="bi bi-trash3 text-danger" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="10" className="text-center">
                Nenhum registro encontrado.
              </td>
            </tr>
          )}
        </tbody>
      </TableContainer>
      <PageFooter>
        <Pagination10 {...setPagination} />
      </PageFooter>
      <ExcelUploadModal
        show={showExcelUploadModal}
        onClose={() => setShowExcelUploadModal(false)}
        onSuccess={() => setShouldFetch(!shouldFetch)}
        dfd={dfd}
        dfdLote={dfdLote}
        loteNum={dfdLote.numLote || dfdLote.id}
      />
      <DfdFindItemModal isOpen={showFindModal} onClose={() => setShowFindModal(false)} dfd={{ ...dfd, tipo: dfd.tipo || location.state?.dfd?.tipo || '' }} dfdLote={dfdLote} onAdded={() => { setShowFindModal(false); setShouldFetch(prev => !prev); }} />
      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
      <ProgressModal
        show={progressState.show}
        title={progressState.title}
        progress={progressState.progress}
        current={progressState.current}
        total={progressState.total}
        errors={progressState.errors}
        onCancel={progressState.onCancel}
      />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default DfdLoteItens;
